import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation } from 'react-router-dom';
import { X, CheckCircle, XCircle, Eye, Hand, Ear, Image, FileText, Plus, Edit, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Search, Building2, Target, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { amsCheckpointService, AmsCheckpoint, UpdateAmsCheckpointForm } from '../../../../services/amsCheckpointService';
import { Asset } from '../../../../types/asset';
import projectTypeService, { ProjectType } from '../../../../services/projectTypeService';
import auditTypeService, { AuditType } from '../../../../services/auditTypeService';
import { assetService } from '../../../../services/assetService';
import documentMasterService from '../../../../services/documentMasterService';
import uomMasterService from '../../../../services/uomMasterService';
import Button from '../../../../shared/components/ui/button/Button';
import Select from '../../../../shared/components/form/Select';
import { CreateAmsCheckpointForm } from '../../../../services/amsCheckpointService';
import { Modal } from '../../../../shared/components/ui/modal';
import RightDrawer from '../../../../shared/components/form/RightDrawer';

interface AmsCheckpointDrawerProps {
  open: boolean;
  onClose: () => void;
  type: 'asset' | 'project';
  asset?: Asset;
  projectType?: {
    _id: string;
    id?: string;
    name: string;
    code?: string;
  };
  projectTypeId?: string;
  /** Render the content inline (no overlay/backdrop/close) — used to embed the
   *  project-checkpoint manager directly in the checkpoints masters page. */
  embedded?: boolean;
}

const formatDateTime = (dateString: string | Date) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    // Format: "MMM DD, YYYY at HH:MM AM/PM"
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  } catch (error) {
    return 'N/A';
  }
};

// Document Type Multi-Select Component
interface DocumentTypeMultiSelectProps {
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

const DocumentTypeMultiSelect: React.FC<DocumentTypeMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Filter options based on search
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if all filtered options are selected
  const allFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(opt => selectedValues.includes(opt.value));

  const handleToggleOption = (value: string) => {
    if (disabled) return;
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    if (allFilteredSelected) {
      const filteredValues = filteredOptions.map(opt => opt.value);
      const newValues = selectedValues.filter(v => !filteredValues.includes(v));
      onChange(newValues);
    } else {
      const filteredValues = filteredOptions.map(opt => opt.value);
      const newValues = [...new Set([...selectedValues, ...filteredValues])];
      onChange(newValues);
    }
  };

  // Get selected option labels with values
  const selectedOptions = selectedValues
    .map(value => {
      const option = options.find(opt => opt.value === value);
      return option ? { value, label: option.label } : null;
    })
    .filter(Boolean) as Array<{ value: string; label: string }>;

  const handleRemoveTag = (valueToRemove: string, e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    const newValues = selectedValues.filter(v => v !== valueToRemove);
    onChange(newValues);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-12 w-full rounded-xl border px-4 py-2 text-base text-gray-800 shadow-sm transition-all duration-200 flex items-center justify-between ${
          disabled
            ? 'bg-gray-50 border-gray-300 cursor-not-allowed'
            : 'bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
        }`}
      >
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 py-1">
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#339274]/10 text-[#339274] rounded-md text-sm font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                {option.label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => handleRemoveTag(option.value, e)}
                    className="hover:text-red-600 transition-colors flex-shrink-0"
                    aria-label={`Remove ${option.label}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="text-gray-400 py-1">
              {disabled ? 'No document types selected' : 'Select document types'}
            </span>
          )}
        </div>
        {!disabled && (
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>

      {!disabled && isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search document types..."
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {filteredOptions.length > 0 && (
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                className="w-full text-left px-3 py-2 text-sm font-medium text-[#339274] hover:bg-[#339274]/10 rounded-lg transition-colors"
              >
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          )}

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <div
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(option.value);
                    }}
                    className={`flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-[#339274]/5' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleOption(option.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded cursor-pointer"
                    />
                    <label className="ml-3 text-sm text-gray-700 cursor-pointer flex-1">
                      {option.label}
                    </label>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No document types found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Validation schema for create form
const createSchema = yup.object({
  name: yup.string()
    .required('Checkpoint name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(200, 'Name must be less than 200 characters')
    .trim(),
  checkpointType: yup.string()
    .required('Checkpoint type is required')
    .oneOf(['asset', 'project'], 'Invalid checkpoint type'),
  projectTypeId: yup.string()
    .required('Project Type is required'),
  assetId: yup.string()
    .when('checkpointType', {
      is: 'asset',
      then: (schema) => schema.required('Asset is required when checkpoint type is "asset"'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  checkpointMeta: yup.object({
    executionType: yup.string()
      .required('Execution type is required')
      .oneOf(['manual', 'visual', 'hearing'], 'Invalid execution type'),
    documentApplicable: yup.boolean().required(),
    documentTypes: yup.array().of(yup.string().trim().max(100)).optional().default([])
      .test('document-types-required', 'At least one document type must be selected when Document Applicable is checked', function(value) {
        const { documentApplicable } = this.parent;
        if (documentApplicable && (!value || value.length === 0)) {
          return this.createError({ message: 'At least one document type must be selected when Document Applicable is checked' });
        }
        return true;
      }),
    designedParameterAvailable: yup.boolean().required(),
    designedParameterCategory: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Category is required when Designed Parameter Available is checked').trim().max(100),
      otherwise: (schema) => schema.optional().trim(),
    }),
    designedParameterUom: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Designed Parameter UOM is required when Designed Parameter Available is checked').trim().max(50),
      otherwise: (schema) => schema.optional().trim(),
    }),
    actualParameter: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Actual Parameter is required when Designed Parameter Available is checked').trim().max(200),
      otherwise: (schema) => schema.optional().trim(),
    }),
    imagesEnabled: yup.boolean().required(),
    remarksEnabled: yup.boolean().required(),
    applicableAuditTypeIds: yup.array().of(yup.string().required()).min(1, 'Select at least one audit type').required(),
  }).required(),
  status: yup.string().oneOf(['active', 'inactive']).default('active'),
}).test('asset-validation', 'Asset should not be provided when checkpoint type is "project"', function(value) {
  if (value.checkpointType === 'project' && value.assetId) {
    return this.createError({
      path: 'assetId',
      message: 'Asset should not be provided when checkpoint type is "project"'
    });
  }
  return true;
});

// Validation schema for edit form
const editSchema = yup.object({
  name: yup.string()
    .required('Checkpoint name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(200, 'Name must be less than 200 characters')
    .trim(),
  checkpointType: yup.string()
    .required('Checkpoint type is required')
    .oneOf(['asset', 'project'], 'Invalid checkpoint type'),
  projectTypeId: yup.string()
    .required('Project Type is required'),
  assetId: yup.string()
    .when('checkpointType', {
      is: 'asset',
      then: (schema) => schema.required('Asset is required when checkpoint type is "asset"'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  checkpointMeta: yup.object({
    executionType: yup.string()
      .required('Execution type is required')
      .oneOf(['manual', 'visual', 'hearing'], 'Invalid execution type'),
    documentApplicable: yup.boolean().required(),
    documentTypes: yup.array().of(yup.string().trim().max(100)).optional().default([])
      .test('document-types-required', 'At least one document type must be selected when Document Applicable is checked', function(value) {
        const { documentApplicable } = this.parent;
        if (documentApplicable && (!value || value.length === 0)) {
          return this.createError({ message: 'At least one document type must be selected when Document Applicable is checked' });
        }
        return true;
      }),
    designedParameterAvailable: yup.boolean().required(),
    designedParameterCategory: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Category is required when Designed Parameter Available is checked').trim().max(100),
      otherwise: (schema) => schema.optional().trim(),
    }),
    designedParameterUom: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Designed Parameter UOM is required when Designed Parameter Available is checked').trim().max(50),
      otherwise: (schema) => schema.optional().trim(),
    }),
    actualParameter: yup.string().when('designedParameterAvailable', {
      is: true,
      then: (schema) => schema.required('Actual Parameter is required when Designed Parameter Available is checked').trim().max(200),
      otherwise: (schema) => schema.optional().trim(),
    }),
    imagesEnabled: yup.boolean().required(),
    remarksEnabled: yup.boolean().required(),
    applicableAuditTypeIds: yup.array().of(yup.string().required()).min(1, 'Select at least one audit type').required(),
  }).required(),
  status: yup.string().oneOf(['active', 'inactive']).default('active'),
}).test('asset-validation', 'Asset should not be provided when checkpoint type is "project"', function(value) {
  if (value.checkpointType === 'project' && value.assetId) {
    return this.createError({
      path: 'assetId',
      message: 'Asset should not be provided when checkpoint type is "project"'
    });
  }
  return true;
});

type EditFormValues = yup.InferType<typeof editSchema>;
type CreateFormValues = yup.InferType<typeof createSchema>;

const defaultCreateValues: CreateFormValues = {
  name: '',
  checkpointType: undefined as any,
  projectTypeId: '',
  assetId: undefined,
  checkpointMeta: {
    executionType: undefined as any,
    documentApplicable: false,
    documentTypes: [],
    designedParameterAvailable: false,
    designedParameterCategory: '',
    designedParameterUom: '',
    actualParameter: '',
    imagesEnabled: false,
    remarksEnabled: false,
    // Default to applying to every currently-active audit type — a checkpoint left
    // unchecked here would otherwise match none of them and never get auto-picked up
    // by any audit. The real active-id list is filled in at form-reset time (see
    // getCreateDefaultValues) once fetched from auditTypeService; this static default
    // is only used before that fetch resolves.
    applicableAuditTypeIds: [],
  },
  status: 'active',
};

const AmsCheckpointDrawer: React.FC<AmsCheckpointDrawerProps> = ({
  open,
  onClose,
  type,
  asset,
  projectType,
  projectTypeId,
  embedded = false,
}) => {
  const [checkpoints, setCheckpoints] = useState<AmsCheckpoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCheckpoint, setExpandedCheckpoint] = useState<string | null>(null);
  const [editingCheckpointId, setEditingCheckpointId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [activeAuditTypes, setActiveAuditTypes] = useState<AuditType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [uomCategories, setUomCategories] = useState<string[]>([]);
  const [uomOptions, setUomOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [editUomOptions, setEditUomOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [showDocumentMasterModal, setShowDocumentMasterModal] = useState(false);
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);
  const [addDocumentName, setAddDocumentName] = useState('');
  const [addDocumentDescription, setAddDocumentDescription] = useState('');
  const location = useLocation();
  const isBulkAmsPage = location.pathname.includes('/bulk-ams');

  const checkpointTypeOptions = [
    { value: 'asset', label: 'Asset' },
    { value: 'project', label: 'Project' },
  ];

  const executionTypeOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'visual', label: 'Visual' },
    { value: 'hearing', label: 'Hearing' },
  ];

  // Form for editing
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<EditFormValues>({
    resolver: yupResolver(editSchema) as any,
    mode: 'onChange',
  });

  // Form for creating
  const {
    control: createControl,
    handleSubmit: handleCreateSubmit,
    watch: watchCreate,
    reset: resetCreate,
    setValue: setCreateValue,
    formState: { errors: createErrors, isDirty: isCreateDirty, isValid: isCreateValid },
  } = useForm<CreateFormValues>({
    resolver: yupResolver(createSchema) as any,
    mode: 'onChange',
    defaultValues: {
      ...defaultCreateValues,
      projectTypeId: projectTypeId || (projectType ? (projectType._id || projectType.id) : ''),
    },
  });

  const watchedCheckpointType = watch('checkpointType');
  const watchedProjectTypeId = watch('projectTypeId');
  const watchedDocumentApplicable = watch('checkpointMeta.documentApplicable');
  const watchedDesignedParameterAvailable = watch('checkpointMeta.designedParameterAvailable');
  const watchedDesignedParameterCategory = watch('checkpointMeta.designedParameterCategory');
  const watchedDocumentTypes = watch('checkpointMeta.documentTypes') || [];
  const watchedName = watch('name');

  // Audit Type "Select All / Deselect All" for the edit form (see create-form equivalent below).
  // Operates over the dynamic list of currently-active audit types rather than a fixed set of fields.
  const watchedApplicableAuditTypeIds = watch('checkpointMeta.applicableAuditTypeIds') || [];
  const allEditAuditTypesChecked = activeAuditTypes.length > 0 &&
    activeAuditTypes.every((auditType) => watchedApplicableAuditTypeIds.includes(auditType._id));
  const toggleAllEditAuditTypes = () => {
    const next = allEditAuditTypesChecked ? [] : activeAuditTypes.map((auditType) => auditType._id);
    setValue('checkpointMeta.applicableAuditTypeIds', next, { shouldDirty: true, shouldValidate: true });
  };

  // Watch create form values
  const watchedCreateCheckpointType = watchCreate('checkpointType');
  const watchedCreateProjectTypeId = watchCreate('projectTypeId');
  const watchedCreateDocumentApplicable = watchCreate('checkpointMeta.documentApplicable');
  const watchedCreateDesignedParameterAvailable = watchCreate('checkpointMeta.designedParameterAvailable');
  const watchedCreateDesignedParameterCategory = watchCreate('checkpointMeta.designedParameterCategory');
  const watchedCreateDocumentTypes = watchCreate('checkpointMeta.documentTypes') || [];
  const watchedCreateName = watchCreate('name');

  // Audit Type "Select All / Deselect All" — unchecking each audit type one at a
  // time is tedious; this toggles them all at once based on whether all are currently checked.
  // Operates over the dynamic list of currently-active audit types rather than a fixed set of fields.
  const watchedCreateApplicableAuditTypeIds = watchCreate('checkpointMeta.applicableAuditTypeIds') || [];
  const allCreateAuditTypesChecked = activeAuditTypes.length > 0 &&
    activeAuditTypes.every((auditType) => watchedCreateApplicableAuditTypeIds.includes(auditType._id));
  const toggleAllCreateAuditTypes = () => {
    const next = allCreateAuditTypesChecked ? [] : activeAuditTypes.map((auditType) => auditType._id);
    setCreateValue('checkpointMeta.applicableAuditTypeIds', next, { shouldDirty: true, shouldValidate: true });
  };

  // Once active audit types load, refresh the create form's default selection (if the
  // create form is open and the user hasn't touched it yet) so it starts out applying
  // to every currently-active audit type, matching the old "default all 4 to true" behavior.
  useEffect(() => {
    if (activeAuditTypes.length > 0 && showCreateForm && !isCreateDirty) {
      setCreateValue(
        'checkpointMeta.applicableAuditTypeIds',
        activeAuditTypes.map((auditType) => auditType._id),
        { shouldDirty: false, shouldValidate: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAuditTypes]);

  useEffect(() => {
    if (open) {
      fetchProjectTypes();
      fetchActiveAuditTypes();
      fetchDocumentMasters();
      fetchUomCategories();
    }
  }, [open]);

  const fetchDocumentMasters = async () => {
    try {
      const options = await documentMasterService.getDocumentMasterDropdownOptions();
      setDocumentTypeOptions(options);
    } catch (error) {
      console.error('Failed to fetch document masters:', error);
      // Set empty array on error to prevent form from breaking
      setDocumentTypeOptions([]);
    }
  };

  const fetchUomCategories = async () => {
    try {
      const categories = await uomMasterService.getCategories();
      setUomCategories(categories);
    } catch (error) {
      console.error('Failed to fetch UOM categories:', error);
      toast.error('Failed to load UOM categories');
      setUomCategories([]);
    }
  };

  const fetchUomsByCategory = async (category: string, isEditForm: boolean = false) => {
    try {
      const options = await uomMasterService.getUomDropdownOptions(category);
      if (isEditForm) {
        setEditUomOptions(options);
      } else {
        setUomOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch UOMs:', error);
      toast.error('Failed to load UOMs for selected category');
      if (isEditForm) {
        setEditUomOptions([]);
      } else {
        setUomOptions([]);
      }
    }
  };

  // Watch for category changes in create form
  useEffect(() => {
    if (watchedCreateDesignedParameterCategory) {
      fetchUomsByCategory(watchedCreateDesignedParameterCategory, false);
    } else {
      setUomOptions([]);
      setCreateValue('checkpointMeta.designedParameterUom', '');
    }
  }, [watchedCreateDesignedParameterCategory, setCreateValue]);

  // Watch for category changes in edit form
  useEffect(() => {
    if (watchedDesignedParameterCategory) {
      fetchUomsByCategory(watchedDesignedParameterCategory, true);
    } else {
      setEditUomOptions([]);
      setValue('checkpointMeta.designedParameterUom', '');
    }
  }, [watchedDesignedParameterCategory, setValue]);

  const handleAddDocumentSubmit = async () => {
    if (!addDocumentName.trim()) {
      toast.error('Document name is required');
      return;
    }

    try {
      setIsCreatingDocument(true);
      const response = await documentMasterService.createDocumentMaster({
        documentName: addDocumentName.trim(),
        documentDescription: addDocumentDescription.trim() || '',
        status: 'Active',
      });
      
      if (response && response.data) {
        const newDocument = response.data;
        // Add the new document to the options
        const newOption = {
          value: newDocument._id || newDocument.id || '',
          label: newDocument.documentName,
        };
        setDocumentTypeOptions(prev => [...prev, newOption]);
        
        // Auto-select the newly created document in the form
        if (showCreateForm) {
          const currentTypes = watchedCreateDocumentTypes || [];
          if (!currentTypes.includes(newOption.value)) {
            setCreateValue('checkpointMeta.documentTypes', [...currentTypes, newOption.value]);
          }
        } else if (editingCheckpointId) {
          const currentTypes = watchedDocumentTypes || [];
          if (!currentTypes.includes(newOption.value)) {
            setValue('checkpointMeta.documentTypes', [...currentTypes, newOption.value]);
          }
        }
        
        toast.success('Document created successfully');
        setShowAddDocument(false);
        setAddDocumentName('');
        setAddDocumentDescription('');
      }
    } catch (error: any) {
      console.error('Failed to create document:', error);
      toast.error(error?.response?.data?.message || 'Failed to create document');
    } finally {
      setIsCreatingDocument(false);
    }
  };

  useEffect(() => {
    if (watchedCheckpointType === 'asset' && watchedProjectTypeId) {
      fetchAssets();
    } else {
      setAssets([]);
      if (watchedCheckpointType === 'project') {
        setValue('assetId', undefined);
      }
    }
  }, [watchedCheckpointType, watchedProjectTypeId, setValue]);

  useEffect(() => {
    if (type === 'asset' && watchedCreateProjectTypeId) {
      fetchAssets(watchedCreateProjectTypeId);
    } else {
      if (type === 'project') {
        setCreateValue('assetId', undefined);
      }
    }
  }, [type, watchedCreateProjectTypeId, setCreateValue]);

  const fetchProjectTypes = async () => {
    try {
      const response = await projectTypeService.getAllProjectTypes();
      let activeProjectTypes: ProjectType[] = [];
      if (response && typeof response === 'object') {
        if ('data' in response && Array.isArray(response.data)) {
          activeProjectTypes = response.data.filter((pt: ProjectType) => pt.status === 'active');
        } else if (Array.isArray(response)) {
          activeProjectTypes = response.filter((pt: ProjectType) => pt.status === 'active');
        }
      }
      setProjectTypes(activeProjectTypes);
    } catch (error) {
      console.error('Failed to fetch project types:', error);
    }
  };

  const fetchActiveAuditTypes = async () => {
    try {
      const auditTypes = await auditTypeService.getAllActiveAuditTypes();
      setActiveAuditTypes(auditTypes);
    } catch (error) {
      console.error('Failed to fetch audit types:', error);
      setActiveAuditTypes([]);
    }
  };

  const fetchAssets = async (projectTypeId?: string) => {
    const ptId = projectTypeId || watchedProjectTypeId;
    if (!ptId) {
      setAssets([]);
      return;
    }
    try {
      const response = await assetService.getAssetsByProjectType(ptId, { limit: 100 });
      let assetsArray: Asset[] = [];
      if (response && typeof response === 'object') {
        if ('assets' in response && Array.isArray(response.assets)) {
          assetsArray = response.assets;
        } else if ('data' in response && Array.isArray(response.data)) {
          assetsArray = response.data;
        } else if (Array.isArray(response)) {
          assetsArray = response;
        }
      }
      setAssets(assetsArray.filter(asset => asset.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      setAssets([]);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCheckpoints();
      // Collapse all checkpoints when drawer opens
      setExpandedCheckpoint(null);
      setEditingCheckpointId(null);
      setShowCreateForm(false);
      // Reset create form with initial values including checkpoint type and project type
      const selectedProjectTypeId = projectTypeId || (projectType ? (projectType._id || projectType.id) : '');
      resetCreate({
        ...defaultCreateValues,
        checkpointType: type, // Set checkpoint type based on drawer type
        projectTypeId: selectedProjectTypeId,
      });
    }
  }, [open, asset, projectType, projectTypeId, type, resetCreate]);

  const fetchCheckpoints = async () => {
    try {
      setIsLoading(true);
      const filters: any = {
        'checkpoint-type': type,
        // Always fetch both active and inactive checkpoints here — this drawer
        // is where users toggle status, so a just-disabled checkpoint must
        // stay visible instead of disappearing (activeDefaultPlugin excludes
        // inactive by default when no status is specified).
        status: 'all',
      };

      if (type === 'asset' && asset) {
        filters['asset-id'] = asset._id || asset.id;
      }

      if (projectTypeId) {
        filters['project-type-id'] = projectTypeId;
      } else if (projectType) {
        filters['project-type-id'] = projectType._id || projectType.id;
      }

      const response = await amsCheckpointService.getAmsCheckpoints(filters);
      setCheckpoints(response.data || []);
    } catch (error) {
      console.error('Failed to fetch AMS checkpoints:', error);
      toast.error('Failed to load checkpoints');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckpointClick = (checkpoint: AmsCheckpoint) => {
    const checkpointId = checkpoint._id || checkpoint.id || '';
    if (expandedCheckpoint === checkpointId) {
      // If clicking the same card, collapse it
      setExpandedCheckpoint(null);
      setEditingCheckpointId(null);
    } else {
      // Expand the clicked card and collapse any other expanded card
      setExpandedCheckpoint(checkpointId);
      setEditingCheckpointId(null);
    }
  };

  const handleEditClick = (checkpoint: AmsCheckpoint, e: React.MouseEvent) => {
    e.stopPropagation();
    const checkpointId = checkpoint._id || checkpoint.id || '';
    setEditingCheckpointId(checkpointId);
    setExpandedCheckpoint(checkpointId);
    
    const projectTypeId = typeof checkpoint.projectTypeId === 'string' 
      ? checkpoint.projectTypeId 
      : checkpoint.projectTypeId._id || checkpoint.projectTypeId.id || '';
    
    const assetId = checkpoint.assetId 
      ? (typeof checkpoint.assetId === 'string' ? checkpoint.assetId : checkpoint.assetId._id || checkpoint.assetId.id || '')
      : undefined;

    reset({
      name: checkpoint.name,
      checkpointType: checkpoint.checkpointType,
      projectTypeId: projectTypeId,
      assetId: assetId,
      checkpointMeta: {
        executionType: checkpoint.checkpointMeta.executionType,
        documentApplicable: checkpoint.checkpointMeta.documentApplicable,
        documentTypes: checkpoint.checkpointMeta.documentTypes || [],
        designedParameterAvailable: checkpoint.checkpointMeta.designedParameterAvailable,
        designedParameterCategory: checkpoint.checkpointMeta.designedParameterCategory || '',
        designedParameterUom: checkpoint.checkpointMeta.designedParameterUom || '',
        actualParameter: checkpoint.checkpointMeta.actualParameter || '',
        imagesEnabled: checkpoint.checkpointMeta.imagesEnabled,
        remarksEnabled: checkpoint.checkpointMeta.remarksEnabled,
        applicableAuditTypeIds: checkpoint.checkpointMeta.applicableAuditTypeIds || [],
      },
      status: checkpoint.status,
    });

    if (checkpoint.checkpointType === 'asset' && projectTypeId) {
      fetchAssets();
    }
  };

  const handleCancelEdit = () => {
    setEditingCheckpointId(null);
    reset();
  };

  // Builds the create form's default values, filling in every currently-active audit
  // type id (falls back to the static empty-array default if none have loaded yet).
  const getCreateDefaultValues = (): CreateFormValues => ({
    ...defaultCreateValues,
    checkpointMeta: {
      ...defaultCreateValues.checkpointMeta,
      applicableAuditTypeIds: activeAuditTypes.map((auditType) => auditType._id),
    },
  });

  const handleCreateClick = () => {
    setShowCreateForm(true);
    setExpandedCheckpoint(null);
    setEditingCheckpointId(null);
    const selectedProjectTypeId = projectTypeId || (projectType ? (projectType._id || projectType.id) : '');
    resetCreate({
      ...getCreateDefaultValues(),
      checkpointType: type, // Set checkpoint type based on drawer type
      projectTypeId: selectedProjectTypeId,
    });
    // Also set the value directly to ensure it's selected in the dropdown
    if (selectedProjectTypeId) {
      setCreateValue('projectTypeId', selectedProjectTypeId, { shouldDirty: false });
      // Trigger assets fetch if checkpoint type is asset
      if (type === 'asset' && selectedProjectTypeId) {
        fetchAssets(selectedProjectTypeId);
      }
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    resetCreate({
      ...getCreateDefaultValues(),
      projectTypeId: projectTypeId || (projectType ? (projectType._id || projectType.id) : ''),
    });
  };

  const onSubmitCreate = async (data: CreateFormValues) => {
    try {
      setIsCreating(true);
      // Use the drawer's type prop to determine checkpointType (not from form data since field is hidden)
      const checkpointTypeValue = type;
      const payload: CreateAmsCheckpointForm = {
        name: data.name.trim(),
        checkpointType: checkpointTypeValue,
        projectTypeId: data.projectTypeId,
        assetId: checkpointTypeValue === 'asset' ? data.assetId : undefined,
        checkpointMeta: {
          executionType: data.checkpointMeta.executionType as 'manual' | 'visual' | 'hearing',
          documentApplicable: data.checkpointMeta.documentApplicable,
          documentTypes: data.checkpointMeta.documentTypes || [],
          designedParameterAvailable: data.checkpointMeta.designedParameterAvailable,
          designedParameterCategory: data.checkpointMeta.designedParameterCategory || '',
          designedParameterUom: data.checkpointMeta.designedParameterUom || '',
          actualParameter: data.checkpointMeta.actualParameter || '',
          imagesEnabled: data.checkpointMeta.imagesEnabled,
          remarksEnabled: data.checkpointMeta.remarksEnabled,
          applicableAuditTypeIds: data.checkpointMeta.applicableAuditTypeIds,
        },
        status: data.status as 'active' | 'inactive',
      };
      await amsCheckpointService.createAmsCheckpoint(payload);
      toast.success('AMS checkpoint created successfully');
      setShowCreateForm(false);
      fetchCheckpoints();
      resetCreate({
        ...getCreateDefaultValues(),
        projectTypeId: projectTypeId || (projectType ? (projectType._id || projectType.id) : ''),
      });
    } catch (error: any) {
      console.error('Failed to create AMS checkpoint:', error);
      toast.error(error?.response?.data?.message || 'Failed to create AMS checkpoint');
    } finally {
      setIsCreating(false);
    }
  };

  const onSubmitEdit = async (data: EditFormValues) => {
    if (!editingCheckpointId) return;

    // Get the checkpoint to use its checkpointType (not from form since field is hidden)
    const checkpoint = checkpoints.find(cp => (cp._id || cp.id) === editingCheckpointId);
    if (!checkpoint) return;

    try {
      setIsSaving(true);
      const checkpointTypeValue = checkpoint.checkpointType;
      const payload: UpdateAmsCheckpointForm = {
        name: data.name.trim(),
        checkpointType: checkpointTypeValue,
        projectTypeId: data.projectTypeId,
        assetId: checkpointTypeValue === 'asset' ? data.assetId : undefined,
        checkpointMeta: {
          executionType: data.checkpointMeta.executionType as 'manual' | 'visual' | 'hearing',
          documentApplicable: data.checkpointMeta.documentApplicable,
          documentTypes: data.checkpointMeta.documentTypes || [],
          designedParameterAvailable: data.checkpointMeta.designedParameterAvailable,
          designedParameterCategory: data.checkpointMeta.designedParameterCategory || '',
          designedParameterUom: data.checkpointMeta.designedParameterUom || '',
          actualParameter: data.checkpointMeta.actualParameter || '',
          imagesEnabled: data.checkpointMeta.imagesEnabled,
          remarksEnabled: data.checkpointMeta.remarksEnabled,
          applicableAuditTypeIds: data.checkpointMeta.applicableAuditTypeIds,
        },
        status: data.status as 'active' | 'inactive',
      };
      await amsCheckpointService.updateAmsCheckpoint(editingCheckpointId, payload);
      toast.success('AMS checkpoint updated successfully');
      setEditingCheckpointId(null);
      fetchCheckpoints();
    } catch (error: any) {
      console.error('Failed to update AMS checkpoint:', error);
      toast.error(error?.response?.data?.message || 'Failed to update AMS checkpoint');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (checkpoint: AmsCheckpoint, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (checkpoint.status === 'active') {
        await amsCheckpointService.inactivateAmsCheckpoint(checkpoint._id || checkpoint.id || '');
        toast.success('Checkpoint inactivated successfully');
      } else {
        await amsCheckpointService.activateAmsCheckpoint(checkpoint._id || checkpoint.id || '');
        toast.success('Checkpoint activated successfully');
      }
      fetchCheckpoints();
    } catch (error) {
      console.error('Failed to toggle checkpoint status:', error);
      toast.error('Failed to update checkpoint status');
    }
  };

  const fetchAssetsForView = async (ptId: string) => {
    try {
      const response = await assetService.getAssetsByProjectType(ptId, { limit: 100 });
      let assetsArray: Asset[] = [];
      if (response && typeof response === 'object') {
        if ('assets' in response && Array.isArray(response.assets)) {
          assetsArray = response.assets;
        } else if ('data' in response && Array.isArray(response.data)) {
          assetsArray = response.data;
        } else if (Array.isArray(response)) {
          assetsArray = response;
        }
      }
      setAssets(assetsArray.filter(asset => asset.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      setAssets([]);
    }
  };

  // Fetch assets when checkpoint is expanded in view mode
  useEffect(() => {
    if (open && expandedCheckpoint && !editingCheckpointId) {
      const checkpoint = checkpoints.find(cp => (cp._id || cp.id) === expandedCheckpoint);
      if (checkpoint && checkpoint.checkpointType === 'asset') {
        const projectTypeId = typeof checkpoint.projectTypeId === 'string' 
          ? checkpoint.projectTypeId 
          : checkpoint.projectTypeId._id || checkpoint.projectTypeId.id || '';
        if (projectTypeId) {
          fetchAssetsForView(projectTypeId);
        }
      }
    }
  }, [open, expandedCheckpoint, editingCheckpointId, checkpoints]);

  if (!open) return null;

  const activeCheckpoints = checkpoints.filter(cp => cp.status === 'active').length;
  const entityName = type === 'asset' ? asset?.name : projectType?.name;
  const entityIcon = type === 'asset' ? Building2 : Target;

  // Helper function to render create form
  const renderCreateForm = () => {
    return (
      <form onSubmit={handleCreateSubmit(onSubmitCreate)} className="space-y-8">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Checkpoint Name *
            </label>
            <div className="relative">
              <Controller
                name="name"
                control={createControl}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                      createErrors.name
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                        : field.value && !createErrors.name
                        ? 'border-green-300 focus:border-green-500 focus:ring-green-500/20'
                        : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                    }`}
                    placeholder="Enter checkpoint name"
                    maxLength={200}
                  />
                )}
              />
              {watchedCreateName && !createErrors.name && (
                <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
              )}
              {createErrors.name && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
              )}
            </div>
            {createErrors.name && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {createErrors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Project Type *
            </label>
            <div className="relative">
              <Controller
                name="projectTypeId"
                control={createControl}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(option) => {
                      const value = option?.value || '';
                      field.onChange(value);
                      if (type === 'asset') {
                        setCreateValue('assetId', undefined, { shouldDirty: true });
                      }
                    }}
                    options={projectTypes.map(pt => ({
                      value: pt._id || pt.id || '',
                      label: pt.name,
                    }))}
                    placeholder="Select project type"
                    className="h-12"
                    isDisabled={true}
                  />
                )}
              />
              {createErrors.projectTypeId && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
              )}
            </div>
            {createErrors.projectTypeId && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {createErrors.projectTypeId.message}
              </p>
            )}
          </div>

          {type === 'asset' && (
            <>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Execution Type *
            </label>
            <div className="relative">
              <Controller
                    name="checkpointMeta.executionType"
                control={createControl}
                render={({ field }) => (
                  <Select
                    value={field.value}
                        onChange={(option) => field.onChange(option?.value || '')}
                        options={executionTypeOptions}
                        placeholder="Select execution type"
                    className="h-12"
                  />
                )}
              />
                  {createErrors.checkpointMeta?.executionType && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
              )}
            </div>
                {createErrors.checkpointMeta?.executionType && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {createErrors.checkpointMeta.executionType.message}
              </p>
            )}
          </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Asset *
              </label>
              <div className="relative">
                {!watchedCreateProjectTypeId ? (
                  <div className="h-12 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-500 shadow-sm">
                    Please select a project type first
                  </div>
                ) : (
                  <Controller
                    name="assetId"
                    control={createControl}
                    render={({ field }) => (
                      <Select
                        value={field.value || ''}
                        onChange={(option) => field.onChange(option?.value || '')}
                        options={assets.map(asset => ({
                          value: asset._id || asset.id || '',
                          label: asset.name,
                        }))}
                        placeholder={assets.length === 0 ? "No assets available" : "Select asset"}
                        className="h-12"
                        isDisabled={assets.length === 0}
                      />
                    )}
                  />
                )}
                {createErrors.assetId && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
              </div>
              {createErrors.assetId && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {createErrors.assetId.message}
                </p>
              )}
            </div>
            </>
          )}
        </div>

        {/* Checkpoint Meta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {type !== 'asset' && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Execution Type *
            </label>
            <div className="relative">
              <Controller
                name="checkpointMeta.executionType"
                control={createControl}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(option) => field.onChange(option?.value || '')}
                    options={executionTypeOptions}
                    placeholder="Select execution type"
                    className="h-12"
                  />
                )}
              />
              {createErrors.checkpointMeta?.executionType && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
              )}
            </div>
            {createErrors.checkpointMeta?.executionType && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {createErrors.checkpointMeta.executionType.message}
              </p>
            )}
          </div>
          )}

          {type !== 'asset' && (
          <div className="flex items-center pt-8">
            <Controller
              name="checkpointMeta.documentApplicable"
              control={createControl}
              render={({ field }) => (
                <input
                  type="checkbox"
                  id="create-documentApplicable"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                />
              )}
            />
            <label htmlFor="create-documentApplicable" className="ml-2 block text-sm font-semibold text-gray-700">
              Document Applicable
            </label>
          </div>
          )}

          {type !== 'asset' && watchedCreateDocumentApplicable && (
            <div className="col-span-2 space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Document Types *
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                <Controller
                  name="checkpointMeta.documentTypes"
                  control={createControl}
                  render={({ field }) => (
                    <DocumentTypeMultiSelect
                      options={documentTypeOptions}
                      selectedValues={watchedCreateDocumentTypes || []}
                      onChange={(values) => {
                        field.onChange(values);
                      }}
                    />
                  )}
                />
                {createErrors.checkpointMeta?.documentTypes && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddDocument(true)}
                  className="h-12 w-12 flex items-center justify-center rounded-xl border border-dashed border-gray-300 hover:border-[#339274] hover:bg-[#339274]/5 text-gray-400 hover:text-[#339274] transition-colors flex-shrink-0"
                  title="Add new document"
                >
                  <Plus size={20} />
                </button>
              </div>
              {createErrors.checkpointMeta?.documentTypes && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {createErrors.checkpointMeta.documentTypes.message}
                </p>
              )}
            </div>
          )}

          {type === 'asset' && (
            <div className="col-span-2 flex items-center">
              <Controller
                name="checkpointMeta.documentApplicable"
                control={createControl}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="create-asset-documentApplicable"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                  />
                )}
              />
              <label htmlFor="create-asset-documentApplicable" className="ml-2 block text-sm font-semibold text-gray-700">
                Document Applicable
              </label>
            </div>
          )}

          {type === 'asset' && watchedCreateDocumentApplicable && (
            <div className="col-span-2 space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Document Types *
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Controller
                    name="checkpointMeta.documentTypes"
                    control={createControl}
                    render={({ field }) => (
                      <DocumentTypeMultiSelect
                        options={documentTypeOptions}
                        selectedValues={watchedCreateDocumentTypes || []}
                        onChange={(values) => {
                          field.onChange(values);
                        }}
                      />
                    )}
                  />
                  {createErrors.checkpointMeta?.documentTypes && (
                    <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowDocumentMasterModal(true)}
                  className="flex-shrink-0 h-12 px-4 bg-[#339274] hover:bg-[#2a7a5f] text-white rounded-xl transition-colors duration-200 flex items-center gap-2 shadow-sm"
                >
                  <Plus size={20} />
                </button>
              </div>
              {createErrors.checkpointMeta?.documentTypes && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {createErrors.checkpointMeta.documentTypes.message}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center">
            <Controller
              name="checkpointMeta.designedParameterAvailable"
              control={createControl}
              render={({ field }) => (
                <input
                  type="checkbox"
                  id="create-designedParameterAvailable"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                />
              )}
            />
            <label htmlFor="create-designedParameterAvailable" className="ml-2 block text-sm font-semibold text-gray-700">
              Designed Parameter Available
            </label>
          </div>

          {/* Category Dropdown - Only show when Designed Parameter Available is checked */}
          {watchedCreateDesignedParameterAvailable && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Designed Parameter Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Controller
                  name="checkpointMeta.designedParameterCategory"
                  control={createControl}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(option) => field.onChange(option?.value || '')}
                      options={uomCategories.map(cat => ({ value: cat, label: cat }))}
                      placeholder="Select Category"
                      className="h-12"
                    />
                  )}
                />
                {createErrors.checkpointMeta?.designedParameterCategory && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
              </div>
              {createErrors.checkpointMeta?.designedParameterCategory && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {createErrors.checkpointMeta.designedParameterCategory.message}
                </p>
              )}
            </div>
          )}

          {/* Designed Parameter UOM Dropdown - Only show when category is selected */}
          {watchedCreateDesignedParameterAvailable && watchedCreateDesignedParameterCategory && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Designed Parameter UOM <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Controller
                  name="checkpointMeta.designedParameterUom"
                  control={createControl}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(option) => field.onChange(option?.value || '')}
                      options={uomOptions}
                      placeholder="Select UOM"
                      className="h-12"
                    />
                  )}
                />
                {createErrors.checkpointMeta?.designedParameterUom && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
              </div>
              {createErrors.checkpointMeta?.designedParameterUom && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {createErrors.checkpointMeta.designedParameterUom.message}
                </p>
              )}
            </div>
          )}

          {/* Actual Parameter - Text Input */}
          {watchedCreateDesignedParameterAvailable && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Actual Parameter <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Controller
                  name="checkpointMeta.actualParameter"
                  control={createControl}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter actual parameter"
                      maxLength={200}
                      className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                        createErrors.checkpointMeta?.actualParameter
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                      }`}
                    />
                  )}
                />
                {createErrors.checkpointMeta?.actualParameter && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
              </div>
              {createErrors.checkpointMeta?.actualParameter && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {createErrors.checkpointMeta.actualParameter.message}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center">
            <Controller
              name="checkpointMeta.imagesEnabled"
              control={createControl}
              render={({ field }) => (
                <input
                  type="checkbox"
                  id="create-imagesEnabled"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                />
              )}
            />
            <label htmlFor="create-imagesEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
              Images Enabled
            </label>
          </div>

          <div className="flex items-center">
            <Controller
              name="checkpointMeta.remarksEnabled"
              control={createControl}
              render={({ field }) => (
                <input
                  type="checkbox"
                  id="create-remarksEnabled"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                />
              )}
            />
            <label htmlFor="create-remarksEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
              Remarks Enabled
            </label>
          </div>

          <div className="md:col-span-2 pt-2 mt-2 border-t border-gray-100 flex items-center justify-between">
            <label className="block text-sm font-semibold text-gray-700">
              Audit Type
            </label>
            <button
              type="button"
              onClick={toggleAllCreateAuditTypes}
              className="text-xs font-medium text-[#339274] hover:underline"
            >
              {allCreateAuditTypesChecked ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="md:col-span-2 flex flex-wrap gap-x-6 gap-y-3">
            {activeAuditTypes.length > 0 ? (
              activeAuditTypes.map((auditType) => {
                const checked = watchedCreateApplicableAuditTypeIds.includes(auditType._id);
                return (
                  <div key={auditType._id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`create-auditType-${auditType._id}`}
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...watchedCreateApplicableAuditTypeIds, auditType._id]
                          : watchedCreateApplicableAuditTypeIds.filter((id) => id !== auditType._id);
                        setCreateValue('checkpointMeta.applicableAuditTypeIds', next, { shouldDirty: true, shouldValidate: true });
                      }}
                      className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                    />
                    <label htmlFor={`create-auditType-${auditType._id}`} className="ml-2 block text-sm font-semibold text-gray-700">
                      {auditType.name}
                    </label>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500">No active audit types found</p>
            )}
          </div>
          {createErrors.checkpointMeta?.applicableAuditTypeIds && (
            <p className="md:col-span-2 flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {createErrors.checkpointMeta.applicableAuditTypeIds.message}
            </p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancelCreate}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isCreating || !isCreateDirty || !isCreateValid}
            className="bg-[#339274] hover:bg-[#29795f] text-white"
          >
            {isCreating 
              ? 'Creating...' 
              : type === 'project' 
                ? 'Create Project Checkpoint' 
                : 'Create Asset Checkpoint'}
          </Button>
        </div>
      </form>
    );
  };

  // Helper function to render inline form fields
  const renderFormFields = (checkpoint: AmsCheckpoint, isEditMode: boolean) => {
    const projectTypeId = typeof checkpoint.projectTypeId === 'string' 
      ? checkpoint.projectTypeId 
      : checkpoint.projectTypeId._id || checkpoint.projectTypeId.id || '';

    const assetId = checkpoint.assetId 
      ? (typeof checkpoint.assetId === 'string' ? checkpoint.assetId : checkpoint.assetId._id || checkpoint.assetId.id || '')
      : undefined;

    const selectedProjectType = projectTypes.find(pt => (pt._id || pt.id) === projectTypeId);
    const selectedAsset = assets.find(a => (a._id || a.id) === assetId);

    if (isEditMode) {
      return (
        <form onSubmit={handleSubmit(onSubmitEdit)} className="space-y-8" onClick={(e) => e.stopPropagation()}>
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Checkpoint Name *
              </label>
              <div className="relative">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                        errors.name
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                          : field.value && !errors.name
                          ? 'border-green-300 focus:border-green-500 focus:ring-green-500/20'
                          : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                      }`}
                      placeholder="Enter checkpoint name"
                      maxLength={200}
                    />
                  )}
                />
                {watchedName && !errors.name && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {errors.name && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {errors.name && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Project Type *
              </label>
              <div className="relative">
                <Controller
                  name="projectTypeId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(option) => {
                        const value = option?.value || '';
                        field.onChange(value);
                        if (checkpoint.checkpointType === 'asset') {
                          setValue('assetId', undefined, { shouldDirty: true });
                        }
                      }}
                      options={projectTypes.map(pt => ({
                        value: pt._id || pt.id || '',
                        label: pt.name,
                      }))}
                      placeholder="Select project type"
                      className="h-12"
                      isDisabled={true}
                    />
                  )}
                />
                {errors.projectTypeId && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
              </div>
              {errors.projectTypeId && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.projectTypeId.message}
                </p>
              )}
            </div>

            {checkpoint.checkpointType === 'asset' && (
              <>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Execution Type *
              </label>
              <div className="relative">
                <Controller
                      name="checkpointMeta.executionType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                          onChange={(option) => field.onChange(option?.value || '')}
                          options={executionTypeOptions}
                          placeholder="Select execution type"
                      className="h-12"
                    />
                  )}
                />
                    {errors.checkpointMeta?.executionType && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
              </div>
                  {errors.checkpointMeta?.executionType && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {errors.checkpointMeta.executionType.message}
                </p>
              )}
            </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Asset *
                </label>
                <div className="relative">
                  {!watchedProjectTypeId ? (
                    <div className="h-12 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-500 shadow-sm">
                      Please select a project type first
                    </div>
                  ) : (
                    <Controller
                      name="assetId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value || ''}
                          onChange={(option) => field.onChange(option?.value || '')}
                          options={assets.map(asset => ({
                            value: asset._id || asset.id || '',
                            label: asset.name,
                          }))}
                          placeholder={assets.length === 0 ? "No assets available" : "Select asset"}
                          className="h-12"
                          isDisabled={assets.length === 0}
                        />
                      )}
                    />
                  )}
                  {errors.assetId && (
                    <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                  )}
                </div>
                {errors.assetId && (
                  <p className="flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {errors.assetId.message}
                  </p>
                )}
              </div>
              </>
            )}
          </div>

          {/* Checkpoint Meta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {checkpoint.checkpointType !== 'asset' && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Execution Type *
              </label>
              <div className="relative">
                <Controller
                  name="checkpointMeta.executionType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(option) => field.onChange(option?.value || '')}
                      options={executionTypeOptions}
                      placeholder="Select execution type"
                      className="h-12"
                    />
                  )}
                />
                {errors.checkpointMeta?.executionType && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                )}
              </div>
              {errors.checkpointMeta?.executionType && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.checkpointMeta.executionType.message}
                </p>
              )}
            </div>
            )}

            {checkpoint.checkpointType !== 'asset' && (
            <div className="flex items-center pt-8">
              <Controller
                name="checkpointMeta.documentApplicable"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="documentApplicable"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                  />
                )}
              />
              <label htmlFor="documentApplicable" className="ml-2 block text-sm font-semibold text-gray-700">
                Document Applicable
              </label>
            </div>
            )}

            {checkpoint.checkpointType !== 'asset' && watchedDocumentApplicable && (
              <div className="col-span-2 space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Document Types *
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                  <Controller
                    name="checkpointMeta.documentTypes"
                    control={control}
                    render={({ field }) => (
                      <DocumentTypeMultiSelect
                        options={documentTypeOptions}
                        selectedValues={watchedDocumentTypes || []}
                        onChange={(values) => {
                          field.onChange(values);
                        }}
                      />
                    )}
                  />
                  {errors.checkpointMeta?.documentTypes && (
                    <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                  )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAddDocument(true)}
                    className="h-12 w-12 flex items-center justify-center rounded-xl border border-dashed border-gray-300 hover:border-[#339274] hover:bg-[#339274]/5 text-gray-400 hover:text-[#339274] transition-colors flex-shrink-0"
                    title="Add new document"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                {errors.checkpointMeta?.documentTypes && (
                  <p className="flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {errors.checkpointMeta.documentTypes.message}
                  </p>
                )}
              </div>
            )}

            {checkpoint.checkpointType === 'asset' && (
              <div className="col-span-2 flex items-center">
                <Controller
                  name="checkpointMeta.documentApplicable"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      id="edit-asset-documentApplicable"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                    />
                  )}
                />
                <label htmlFor="edit-asset-documentApplicable" className="ml-2 block text-sm font-semibold text-gray-700">
                  Document Applicable
                </label>
              </div>
            )}

            {checkpoint.checkpointType === 'asset' && watchedDocumentApplicable && (
              <div className="col-span-2 space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Document Types *
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Controller
                      name="checkpointMeta.documentTypes"
                      control={control}
                      render={({ field }) => (
                        <DocumentTypeMultiSelect
                          options={documentTypeOptions}
                          selectedValues={watchedDocumentTypes || []}
                          onChange={(values) => {
                            field.onChange(values);
                          }}
                        />
                      )}
                    />
                    {errors.checkpointMeta?.documentTypes && (
                      <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAddDocument(true)}
                    className="h-12 w-12 flex items-center justify-center rounded-xl border border-dashed border-gray-300 hover:border-[#339274] hover:bg-[#339274]/5 text-gray-400 hover:text-[#339274] transition-colors flex-shrink-0"
                    title="Add new document"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                {errors.checkpointMeta?.documentTypes && (
                  <p className="flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {errors.checkpointMeta.documentTypes.message}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center">
              <Controller
                name="checkpointMeta.designedParameterAvailable"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="designedParameterAvailable"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                  />
                )}
              />
              <label htmlFor="designedParameterAvailable" className="ml-2 block text-sm font-semibold text-gray-700">
                Designed Parameter Available
              </label>
            </div>

            {/* Category Dropdown - Only show when Designed Parameter Available is checked */}
            {watchedDesignedParameterAvailable && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Designed Parameter Category <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Controller
                    name="checkpointMeta.designedParameterCategory"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={(option) => field.onChange(option?.value || '')}
                        options={uomCategories.map(cat => ({ value: cat, label: cat }))}
                        placeholder="Select Category"
                        className="h-12"
                      />
                    )}
                  />
                  {errors.checkpointMeta?.designedParameterCategory && (
                    <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                  )}
                </div>
                {errors.checkpointMeta?.designedParameterCategory && (
                  <p className="flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {errors.checkpointMeta.designedParameterCategory.message}
                  </p>
                )}
              </div>
            )}

            {/* Designed Parameter UOM Dropdown - Only show when category is selected */}
            {watchedDesignedParameterAvailable && watchedDesignedParameterCategory && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Designed Parameter UOM <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Controller
                    name="checkpointMeta.designedParameterUom"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={(option) => field.onChange(option?.value || '')}
                        options={editUomOptions}
                        placeholder="Select UOM"
                        className="h-12"
                      />
                    )}
                  />
                  {errors.checkpointMeta?.designedParameterUom && (
                    <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                  )}
                </div>
                {errors.checkpointMeta?.designedParameterUom && (
                  <p className="flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {errors.checkpointMeta.designedParameterUom.message}
                  </p>
                )}
              </div>
            )}

            {/* Actual Parameter - Text Input */}
            {watchedDesignedParameterAvailable && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Actual Parameter <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Controller
                    name="checkpointMeta.actualParameter"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder="Enter actual parameter"
                        maxLength={200}
                        className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                          errors.checkpointMeta?.actualParameter
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                        }`}
                      />
                    )}
                  />
                  {errors.checkpointMeta?.actualParameter && (
                    <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                  )}
                </div>
                {errors.checkpointMeta?.actualParameter && (
                  <p className="flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {errors.checkpointMeta.actualParameter.message}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center">
              <Controller
                name="checkpointMeta.imagesEnabled"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="imagesEnabled"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                  />
                )}
              />
              <label htmlFor="imagesEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
                Images Enabled
              </label>
            </div>

            <div className="flex items-center">
              <Controller
                name="checkpointMeta.remarksEnabled"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="remarksEnabled"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                  />
                )}
              />
              <label htmlFor="remarksEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
                Remarks Enabled
              </label>
            </div>

            <div className="md:col-span-2 pt-2 mt-2 border-t border-gray-100 flex items-center justify-between">
              <label className="block text-sm font-semibold text-gray-700">
                Audit Type
              </label>
              <button
                type="button"
                onClick={toggleAllEditAuditTypes}
                className="text-xs font-medium text-[#339274] hover:underline"
              >
                {allEditAuditTypesChecked ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div className="md:col-span-2 flex flex-wrap gap-x-6 gap-y-3">
              {activeAuditTypes.length > 0 ? (
                activeAuditTypes.map((auditType) => {
                  const checked = watchedApplicableAuditTypeIds.includes(auditType._id);
                  return (
                    <div key={auditType._id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`edit-auditType-${auditType._id}`}
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...watchedApplicableAuditTypeIds, auditType._id]
                            : watchedApplicableAuditTypeIds.filter((id) => id !== auditType._id);
                          setValue('checkpointMeta.applicableAuditTypeIds', next, { shouldDirty: true, shouldValidate: true });
                        }}
                        className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                      />
                      <label htmlFor={`edit-auditType-${auditType._id}`} className="ml-2 block text-sm font-semibold text-gray-700">
                        {auditType.name}
                      </label>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500">No active audit types found</p>
              )}
            </div>
            {errors.checkpointMeta?.applicableAuditTypeIds && (
              <p className="md:col-span-2 flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.checkpointMeta.applicableAuditTypeIds.message}
              </p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving || !isDirty || !isValid}
              className="bg-[#339274] hover:bg-[#29795f] text-white"
            >
              {isSaving ? 'Updating...' : 'Update Checkpoint'}
            </Button>
          </div>
        </form>
      );
    } else {
      // View mode (read-only)
      return (
        <div className="space-y-8" onClick={(e) => e.stopPropagation()}>
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Checkpoint Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={checkpoint.name}
                  disabled
                  className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 shadow-sm cursor-not-allowed border-gray-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Project Type *
              </label>
              <div className="relative">
                <Select
                  value={selectedProjectType ? {
                    value: selectedProjectType._id || selectedProjectType.id || '',
                    label: selectedProjectType.name,
                  } : undefined}
                  onChange={() => {}}
                  options={projectTypes.map(pt => ({
                    value: pt._id || pt.id || '',
                    label: pt.name,
                  }))}
                  placeholder="Select project type"
                  className="h-12"
                  isDisabled={true}
                />
              </div>
            </div>

            {checkpoint.checkpointType === 'asset' && (
              <>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Execution Type *
              </label>
              <div className="relative">
                <Select
                      value={executionTypeOptions.find(opt => opt.value === checkpoint.checkpointMeta.executionType)}
                  onChange={() => {}}
                      options={executionTypeOptions}
                      placeholder="Select execution type"
                  className="h-12"
                  isDisabled={true}
                />
              </div>
            </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Asset *
                </label>
                <div className="relative">
                  {!projectTypeId ? (
                    <div className="h-12 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-500 shadow-sm">
                      Please select a project type first
                    </div>
                  ) : (
                    <Select
                      value={selectedAsset ? {
                        value: selectedAsset._id || selectedAsset.id || '',
                        label: selectedAsset.name,
                      } : undefined}
                      onChange={() => {}}
                      options={assets.map(asset => ({
                        value: asset._id || asset.id || '',
                        label: asset.name,
                      }))}
                      placeholder={assets.length === 0 ? "No assets available" : "Select asset"}
                      className="h-12"
                      isDisabled={true}
                    />
                  )}
                </div>
              </div>
              </>
            )}
          </div>

          {/* Checkpoint Meta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {checkpoint.checkpointType !== 'asset' && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Execution Type *
              </label>
              <div className="relative">
                <Select
                  value={executionTypeOptions.find(opt => opt.value === checkpoint.checkpointMeta.executionType)}
                  onChange={() => {}}
                  options={executionTypeOptions}
                  placeholder="Select execution type"
                  className="h-12"
                  isDisabled={true}
                />
              </div>
            </div>
            )}

            {checkpoint.checkpointType !== 'asset' && (
            <div className="flex items-center pt-8">
              <input
                type="checkbox"
                id="documentApplicable"
                checked={checkpoint.checkpointMeta.documentApplicable}
                disabled
                className="h-4 w-4 text-[#339274] border-gray-300 rounded cursor-not-allowed"
              />
              <label htmlFor="documentApplicable" className="ml-2 block text-sm font-semibold text-gray-700">
                Document Applicable
              </label>
            </div>
            )}

            {checkpoint.checkpointType !== 'asset' && checkpoint.checkpointMeta.documentApplicable && (
              <div className="col-span-2 space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Document Types *
                </label>
                <div className="relative">
                  <DocumentTypeMultiSelect
                    options={documentTypeOptions}
                    selectedValues={checkpoint.checkpointMeta.documentTypes || []}
                    onChange={() => {}}
                    disabled={true}
                  />
                </div>
              </div>
            )}

            {checkpoint.checkpointType === 'asset' && (
              <div className="col-span-2 flex items-center">
                <input
                  type="checkbox"
                  id="view-asset-documentApplicable"
                  checked={checkpoint.checkpointMeta.documentApplicable}
                  disabled
                  className="h-4 w-4 text-[#339274] border-gray-300 rounded cursor-not-allowed"
                />
                <label htmlFor="view-asset-documentApplicable" className="ml-2 block text-sm font-semibold text-gray-700">
                  Document Applicable
                </label>
              </div>
            )}

            {checkpoint.checkpointType === 'asset' && checkpoint.checkpointMeta.documentApplicable && (
              <div className="col-span-2 space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Document Types *
                </label>
                <div className="relative">
                  <DocumentTypeMultiSelect
                    options={documentTypeOptions}
                    selectedValues={checkpoint.checkpointMeta.documentTypes || []}
                    onChange={() => {}}
                    disabled={true}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="designedParameterAvailable"
                checked={checkpoint.checkpointMeta.designedParameterAvailable}
                disabled
                className="h-4 w-4 text-[#339274] border-gray-300 rounded cursor-not-allowed"
              />
              <label htmlFor="designedParameterAvailable" className="ml-2 block text-sm font-semibold text-gray-700">
                Designed Parameter Available
              </label>
            </div>

            {/* Category - Only show when Designed Parameter Available is checked */}
            {checkpoint.checkpointMeta.designedParameterAvailable && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Designed Parameter Category
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={checkpoint.checkpointMeta.designedParameterCategory || ''}
                    disabled
                    placeholder="Category"
                    className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 shadow-sm cursor-not-allowed border-gray-300"
                  />
                </div>
              </div>
            )}

            {/* Designed Parameter UOM - Only show when Designed Parameter Available is checked */}
            {checkpoint.checkpointMeta.designedParameterAvailable && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Designed Parameter UOM
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={checkpoint.checkpointMeta.designedParameterUom || ''}
                    disabled
                    placeholder="UOM"
                    className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 shadow-sm cursor-not-allowed border-gray-300"
                  />
                </div>
              </div>
            )}

            {/* Actual Parameter - Only show when Designed Parameter Available is checked */}
            {checkpoint.checkpointMeta.designedParameterAvailable && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Actual Parameter
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={checkpoint.checkpointMeta.actualParameter || ''}
                    disabled
                    placeholder="Actual Parameter"
                    className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 shadow-sm cursor-not-allowed border-gray-300"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="imagesEnabled"
                checked={checkpoint.checkpointMeta.imagesEnabled}
                disabled
                className="h-4 w-4 text-[#339274] border-gray-300 rounded cursor-not-allowed"
              />
              <label htmlFor="imagesEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
                Images Enabled
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="remarksEnabled"
                checked={checkpoint.checkpointMeta.remarksEnabled}
                disabled
                className="h-4 w-4 text-[#339274] border-gray-300 rounded cursor-not-allowed"
              />
              <label htmlFor="remarksEnabled" className="ml-2 block text-sm font-semibold text-gray-700">
                Remarks Enabled
              </label>
            </div>

            <div className="md:col-span-2 pt-2 mt-2 border-t border-gray-100">
              <label className="block text-sm font-semibold text-gray-700">
                Audit Type
              </label>
            </div>

            <div className="md:col-span-2 flex flex-wrap gap-x-6 gap-y-3">
              {(checkpoint.checkpointMeta.applicableAuditTypeIds || []).length > 0 ? (
                (checkpoint.checkpointMeta.applicableAuditTypeIds || []).map((auditTypeId) => {
                  const auditType = activeAuditTypes.find((at) => at._id === auditTypeId);
                  return (
                    <div key={auditTypeId} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`view-auditType-${auditTypeId}`}
                        checked
                        disabled
                        className="h-4 w-4 text-[#339274] border-gray-300 rounded cursor-not-allowed"
                      />
                      <label htmlFor={`view-auditType-${auditTypeId}`} className="ml-2 block text-sm font-semibold text-gray-700">
                        {auditType ? auditType.name : auditTypeId}
                      </label>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500">No audit types selected</p>
              )}
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            {!embedded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
                onClick={onClose}
              />
            )}
            <motion.div
              initial={embedded ? false : { x: '100%' }}
              animate={embedded ? undefined : { x: 0 }}
              exit={embedded ? undefined : { x: '100%' }}
              transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
              className={embedded
                ? 'relative w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'
                : 'fixed inset-y-0 right-0 z-50 w-full max-w-4xl overflow-y-auto bg-white shadow-xl'}
            >
              <div className={embedded ? 'flex flex-col' : 'flex flex-col h-full'}>
                        {/* Header */}
                        <div className={`flex items-center justify-between p-6 border-b border-gray-200 bg-white ${embedded ? '' : 'sticky top-0 z-10'}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#339274]/20 rounded-xl flex items-center justify-center">
                              {React.createElement(entityIcon, { size: 24, className: 'text-[#339274]' })}
                            </div>
                            <div>
                              <h2 className="text-xl font-semibold text-gray-900">
                                {entityName} - AMS Checkpoints
                              </h2>
                              <p className="text-sm text-gray-600">
                                {checkpoints.length} checkpoint{checkpoints.length !== 1 ? 's' : ''} ({activeCheckpoints} active)
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={handleCreateClick}
                              size="sm"
                              className="bg-[#339274] hover:bg-[#29795f] text-white"
                              disabled={showCreateForm}
                            >
                              <Plus size={16} className="mr-1" />
                              {type === 'project' ? 'Create Project Checkpoint' : 'Create Asset Checkpoint'}
                            </Button>
                            {!embedded && (
                              <button
                                onClick={onClose}
                                className="p-2 text-gray-400 hover:text-gray-500 rounded-lg hover:bg-gray-100"
                              >
                                <X size={20} />
                              </button>
                            )}
                          </div>
                        </div>

                {/* Content */}
                <div className="flex-1 p-6 overflow-y-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-7 h-7 border-2 border-blue-100 border-t-[#339274] rounded-full animate-spin" />
                        <span className="text-sm font-medium text-gray-700">Loading checkpoints...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Create Form */}
                      {showCreateForm && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25, ease: 'easeInOut' }}
                          className="bg-white rounded-lg border border-[#339274]/30 shadow-sm p-6"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Create New Checkpoint</h3>
                            <button
                              onClick={handleCancelCreate}
                              className="p-1.5 text-gray-400 hover:text-gray-500 rounded-lg hover:bg-gray-100"
                            >
                              <X size={18} />
                            </button>
                          </div>
                          {renderCreateForm()}
                        </motion.div>
                      )}

                      {checkpoints.length === 0 && !showCreateForm ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center">
                          <FileText size={48} className="text-gray-300 mb-4" />
                          <h3 className="text-lg font-semibold text-gray-700 mb-2">No checkpoints found</h3>
                          <p className="text-sm text-gray-500">No checkpoints available for this selection</p>
                        </div>
                      ) : (
                        checkpoints.length > 0 && (
                          <div className="space-y-3">
                            {checkpoints.map((checkpoint) => {
                        const checkpointId = checkpoint._id || checkpoint.id || '';
                        const isExpanded = expandedCheckpoint === checkpointId;
                        const isEditing = editingCheckpointId === checkpointId;

                        return (
                          <div
                            key={checkpointId}
                            className={`bg-white rounded-lg border shadow-sm transition-all duration-200 ${
                              isExpanded
                                ? 'border-[#339274]/30 bg-[#f9fafb]'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {/* Header */}
                            <div className={`p-4 flex items-start justify-between ${!isExpanded ? 'cursor-pointer' : ''}`} onClick={() => !isEditing && handleCheckpointClick(checkpoint)}>
                              <div className="flex items-start gap-3 flex-1">
                                <div className="mt-0.5">
                                  {checkpoint.status === 'active' ? (
                                    <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                                  ) : (
                                    <XCircle size={18} className="text-red-600 flex-shrink-0" />
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 leading-tight mb-2">
                                    {checkpoint.name}
                                  </h4>

                                  {/* Metadata */}
                                  <div className="flex flex-wrap items-start gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                    <div className="flex flex-col">
                                      <span>
                                        Created by{' '}
                                        <span className="font-medium text-gray-700">
                                          {typeof checkpoint.createdBy === 'object'
                                            ? checkpoint.createdBy.name
                                            : 'N/A'}
                                        </span>
                                      </span>
                                      {checkpoint.createdAt && (
                                        <span className="text-gray-400 mt-0.5">
                                          {formatDateTime(checkpoint.createdAt)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronUp size={16} className="text-gray-400" />
                                ) : (
                                  <ChevronDown size={16} className="text-gray-400" />
                                )}

                                {!isEditing && (
                                  <button
                                    onClick={(e) => handleEditClick(checkpoint, e)}
                                    className="p-1.5 rounded-md text-gray-400 hover:text-[#339274] hover:bg-[#339274]/10"
                                  >
                                    <Edit size={14} />
                                  </button>
                                )}

                                {/* Toggle Switch for Active/Inactive */}
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => handleToggleStatus(checkpoint, e)}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                                      checkpoint.status === 'active'
                                        ? 'bg-[#339274]'
                                        : 'bg-gray-300'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                        checkpoint.status === 'active' ? 'translate-x-5' : 'translate-x-0.5'
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Content - View or Edit Form */}
                            {isExpanded && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.25, ease: 'easeInOut' }}
                                className="border-t border-gray-100 bg-white p-6"
                              >
                                {renderFormFields(checkpoint, isEditing)}
                              </motion.div>
                            )}
                          </div>
                        );
                      })}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add Document Form - Modal for checkpoints page, Drawer for bulk-ams page */}
      {isBulkAmsPage ? (
        <RightDrawer
          open={showAddDocument}
          onClose={() => {
            setShowAddDocument(false);
            setAddDocumentName('');
            setAddDocumentDescription('');
          }}
          title="Add New Document"
          size="md"
          footer={
            <div className="flex justify-end gap-3 px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddDocument(false);
                  setAddDocumentName('');
                  setAddDocumentDescription('');
                }}
                disabled={isCreatingDocument}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddDocumentSubmit}
                disabled={isCreatingDocument || !addDocumentName.trim()}
                className="bg-[#339274] hover:bg-[#29795f] text-white"
              >
                {isCreatingDocument ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Document'
                )}
              </Button>
            </div>
          }
        >
          <AddDocumentFormContent
            documentName={addDocumentName}
            setDocumentName={setAddDocumentName}
            documentDescription={addDocumentDescription}
            setDocumentDescription={setAddDocumentDescription}
          />
        </RightDrawer>
      ) : (
        <Modal
          isOpen={showAddDocument}
          onClose={() => {
            setShowAddDocument(false);
            setAddDocumentName('');
            setAddDocumentDescription('');
          }}
          className="max-w-md"
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Add New Document</h2>
            <AddDocumentFormContent
              documentName={addDocumentName}
              setDocumentName={setAddDocumentName}
              documentDescription={addDocumentDescription}
              setDocumentDescription={setAddDocumentDescription}
            />
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddDocument(false);
                  setAddDocumentName('');
                  setAddDocumentDescription('');
                }}
                disabled={isCreatingDocument}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddDocumentSubmit}
                disabled={isCreatingDocument || !addDocumentName.trim()}
                className="bg-[#339274] hover:bg-[#29795f] text-white"
              >
                {isCreatingDocument ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Document'
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

// Add Document Form Content Component
interface AddDocumentFormContentProps {
  documentName: string;
  setDocumentName: (value: string) => void;
  documentDescription: string;
  setDocumentDescription: (value: string) => void;
}

const AddDocumentFormContent: React.FC<AddDocumentFormContentProps> = ({
  documentName,
  setDocumentName,
  documentDescription,
  setDocumentDescription,
}) => {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Document Name *
        </label>
        <input
          type="text"
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          placeholder="Enter document name"
          maxLength={200}
          className="h-12 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 focus:border-[#339274] focus:ring-[#339274]/20"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Document Description
        </label>
        <textarea
          value={documentDescription}
          onChange={(e) => setDocumentDescription(e.target.value)}
          placeholder="Enter document description (optional)"
          maxLength={500}
          rows={4}
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 focus:border-[#339274] focus:ring-[#339274]/20 resize-none"
        />
      </div>
    </div>
  );
};

export default AmsCheckpointDrawer;
